import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { removeVerificationRequest } from 'store/verificationRequest';
import { ThunkConfirmValidation } from 'store/verificationRequest/thunks';
import {
  VerificationErrorSelector,
  VerificationPendingSelector,
  VerificationRequestSelector,
} from 'store/verificationRequest/selectors';

import Button from 'components/Button/Button';
import { IVerifyCodePayload } from 'services/verificationRequest/types';

import useStyles from './styles';

enum verificationTypes {
  codeFromPortal = 'codeFromPortal',
  codeFromSms = 'codeFromSms',
}

const VerificationRequest: FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const verificationRequest = useAppSelector(VerificationRequestSelector);
  const verificationIsPending = useAppSelector(VerificationPendingSelector);
  const verificationError = useAppSelector(VerificationErrorSelector);

  const [time, setTime] = useState('00:00');
  const [codeInput, setCodeInput] = useState('');
  const [verificationType, setVerificationType] = useState(verificationTypes.codeFromPortal);
  const [ids, setIds] = useState<number[]>([]);

  const removeComponent = useCallback(() => {
    dispatch(removeVerificationRequest());
  }, []);

  const startTimer = (duration: number): void => {
    let minute = duration;
    let sec = 0;
    const timer = window.setInterval(() => {
      if (minute === 0 && sec === 0) {
        clearInterval(timer);
        removeComponent();
        return;
      }

      if (sec === 0) {
        minute--;
        sec = 60;
      }

      sec--;
      setTime(`${minute}:${sec < 10 ? `0${sec}` : sec}`);
    }, 1000);

    setIds([...ids, timer]);
  };

  useEffect(() => {
    if (ids.length > 0) {
      ids.forEach(i => {
        clearInterval(i);
      });
    }
    setVerificationType(
      verificationRequest?.code ? verificationTypes.codeFromPortal : verificationTypes.codeFromSms,
    );
    startTimer(verificationRequest?.durationMin ?? 0);
  }, [verificationRequest]);

  const code = useMemo(() => {
    switch (verificationType) {
      case verificationTypes.codeFromPortal:
        return verificationRequest?.code ?? 'empty';
      case verificationTypes.codeFromSms:
        return codeInput;
      default:
        return 'unknown code';
    }
  }, [verificationRequest, codeInput, verificationType]);

  const onVerify = useCallback(() => {
    const payload: IVerifyCodePayload = {
      crmId: verificationRequest?.crmId ?? 0,
      code,
    };
    dispatch(ThunkConfirmValidation(payload));
  }, [verificationRequest, dispatch, codeInput, verificationType]);

  const getBody = useMemo(() => {
    switch (verificationType) {
      case verificationTypes.codeFromPortal:
        return (
          <>
            <span>Validation requested</span>
            <span>&nbsp;{verificationRequest?.phoneNumber}.&nbsp;</span>
          </>
        );
      case verificationTypes.codeFromSms:
        return (
          <>
            <span>Input validation code</span>
            <input
              onChange={e => {
                setCodeInput(e.target.value);
              }}
              value={codeInput}
              id="verification-code"
              className={classes.verificationCodeInput}
            />
          </>
        );
      default:
        return null;
    }
  }, [verificationType, codeInput, verificationRequest]);

  if (!verificationRequest) return null;
  return (
    <div className={classes.container}>
      <div>{getBody}</div>
      <div>Expires in {time}&nbsp;</div>
      <div className={classes.buttons}>
        <Button
          text="Cancel"
          onClick={removeComponent}
          disabled={verificationIsPending}
          color="white"
          textColor="black"
        />

        <Button text="Confirm" onClick={onVerify} type="reset" disabled={verificationIsPending} />
      </div>
      {verificationError && <div style={{ color: 'red' }}>{verificationError.split(':')[1]}</div>}
    </div>
  );
};

export default VerificationRequest;
